import React, {
    useCallback,
    useEffect,
    useState,
    useContext
} from 'react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import _ from 'lodash';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { MLEndorsementFlowUtil, MLEndorsementUtil } from 'ml-portals-util-js';
import { MLNotificationBlockUtil, MLPupLineValidatorUtil } from 'ml-portals-util-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import appConfig from 'app-config';
import { ServiceManager } from '@jutro/legacy/services';
import { MLNavigationStepsTrackingService } from 'ml-services-common-react';
import { useModal } from '@jutro/components';
import { FormModalPopover } from 'ml-components-platform-react';
import { LocationPopupComponent, UnderlyingPolicyComponent } from 'ml-capability-quoteandbind-common-react';
import addUnderlyingPolicyPopupMetadata from '../../../ml-capability-quoteandbind-common-react/pages/UnderlyingPolicies/AddEditUnderlyingPolicyPopup.metadata.json5';
import metadata from './LocationPagePolChange.metadata.json5';


import messages from './LocationPagePolChange.messages';
import styles from './LocationPagePolChange.module.scss';
import { ScrollToError } from '@jutro/legacy/wizard-next';

const alertNames = {
    addedLocationName: 'addedLocation',
    primaryLocationError: 'primaryLocationError',
    locationAnimalExclusionWarning: 'PUPPolicyChangeLocationAnimalExclusionWarning'
};

const LocationPagePolChange = (props) => {
    const modalApi = useModal();
    const {
        wizardData,
        updateWizardData,
        goNext,
        currentStepIndex,
        steps,
        currentStep,
        changeNextSteps
    } = props;
    const { submissionVM, alertMessages } = wizardData;
    const { EndorsementService } = useDependencies('EndorsementService');
    const viewModelService = useContext(ViewModelServiceContext);
    const localeService = ServiceManager.getService('locale-service');
    const translator = useTranslator();
    const { authHeader } = useAuthentication();
    const { refreshData } = useDataRefresh();

    const {
        showProgressDialog
    } = MLModalService();

    const { validatePrimaryLocation } = MLPupLineValidatorUtil();
    const MLAlertService = MLNotificationBlockUtil();
    const {
        clearManyBlockAlerts,
        clearBlockAlerts,
        addErrorAlert,
        addWarningAlert
    } = MLNotificationBlockUtil();

    const isGPA = appConfig.portalCode === 'GPA';
    const pupModel = _.get(submissionVM, 'lobData.personalUmbrella');
    const underlyingPoliciesVM = _.get(submissionVM, 'lobData.personalUmbrella.underlyingPolicies');
    const locationsVM = _.get(submissionVM, 'lobData.personalUmbrella.coverables.locations');
    const isModernProduct = _.get(submissionVM, 'isModernProduct_Ext.value', false);
    const policyAddress = _.get(submissionVM, 'baseData.policyAddress');
    const productCode = _.get(submissionVM, 'baseData.productCode.value', '');
    const [location, setLocation] = useState(viewModelService.create({
        address: {country: localeService.getDefaultCountryCode()}
    }, 'pc', 'metlife.edge.capabilities.common.plugin.dto.PupLocationDTOML'));

    const [nonEditingLocation, setNonEditingLocation] = useState([]);
    const [primaryLocationErrorMessage, setPrimaryLocationErrorMessage] = useState(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);

    const { onValidate } = useValidation('LocationPagePolChange');

    const updateWizardSubmissionData = useCallback((vm) => {
        _.set(wizardData, 'submissionVM', vm);
        updateWizardData(wizardData);
    }, [updateWizardData, wizardData]);

    const setSelectedLocation = (noEditLocPublicIDs) => {
        let index = -1;
        const { additionalData } = currentStep;

        locationsVM.value.forEach((loc, i) => {
            if (noEditLocPublicIDs.findIndex((noEditLocPublicID) => noEditLocPublicID === loc.publicID) === -1) {
                index = i;
                loc.isSelected = true;
            }
        });

        if (index !== -1) {
            return locationsVM.value[index];
        }

        if (additionalData) {
            const selectedIndex = locationsVM.value.findIndex((loc) => loc.publicID === additionalData.publicID);

            return locationsVM.value[selectedIndex];
        }


    };

    const setLocationVM = (locationToSet) => {
        if (locationToSet.address) {
            locationToSet.address = viewModelService.create(locationToSet.address, 'pc', 'edge.capabilities.address.dto.AddressDTO').value;
        } else {
            locationToSet.address = viewModelService.create({country: localeService.getDefaultCountryCode()}, 'pc', 'edge.capabilities.address.dto.AddressDTO').value;
        }
        locationToSet.isDwellingOccupancyRequired_ML = true;
        locationToSet.isDwellingUsageRequired_ML = true;
        return viewModelService.create(locationToSet, 'pc', 'metlife.edge.capabilities.common.plugin.dto.PupLocationDTOML');
    };

    const setFixIdsOfNonEditingLocations = () => {
        const noEditLocPublicIDs = [];
        locationsVM.value.forEach((loc) => {
            if (loc.publicID && !loc.isSelected) {
                noEditLocPublicIDs.push(loc.publicID);
            }
        });
        setNonEditingLocation(noEditLocPublicIDs);
        return noEditLocPublicIDs;
    };

    const setScopeVariables = (noEditLocPublicIDs) => {
        let selectedLocationVM = location;
        const locationToSet = setSelectedLocation(noEditLocPublicIDs);
        if (locationToSet) {
            selectedLocationVM = setLocationVM(locationToSet);
            setLocation(selectedLocationVM);
        }

        if (!selectedLocationVM.value.isFromQuotePage) {
            if (selectedLocationVM.value.isInEditMode) {
                MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.editLocations), false, currentStep.mlStepName, authHeader);
            } else {
                MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.addLocations), false, currentStep.mlStepName, authHeader);
            }
        }
    };

    const initLocationPage = () => {
        const noEditLocPublicIDs = setFixIdsOfNonEditingLocations();
        setScopeVariables(noEditLocPublicIDs);
    };

    useEffect(() => {
        if (MLEndorsementFlowUtil.startingPointPageLoadIsSkipping(submissionVM, goNext, steps, currentStepIndex, changeNextSteps)) {
            return;
        };
        initLocationPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getNextStepText = () => {
        return (
            <FormattedMessage
                {...messages.nextBaseDescription}
                values={{
                    bold: (
                        <strong>{translator(messages.nextDescriptionBold)}</strong>
                    )
                }}
            />
        );
    };

    const syncExposure = (selectedLocation) => {
        const selectedLocIndex = locationsVM.value.findIndex((loc) => nonEditingLocation.indexOf(loc.publicID) === -1);
        _.set(locationsVM, `value[${selectedLocIndex}]`, selectedLocation);
        return EndorsementService.saveEndorsement([submissionVM.value], authHeader).then((updatedEndorsementData) => {
            const policyChange = new PolicyChange(Object.assign({}, updatedEndorsementData));
            const updatedEndorsementVM = viewModelService.create(policyChange, 'pc', 'edge.capabilities.policychange.dto.PolicyChangeDataDTO');
            _.set(wizardData, 'submissionVM', updatedEndorsementVM);

            return updatedEndorsementVM;
        }).catch(() => {
            return submissionVM;
        });
    };

    const syncLocation = (selectedLocation) => {
        return syncExposure(selectedLocation).then((updatedendorsementVM) => {
            let index = 0;
            const newLocationsVM = updatedendorsementVM.lobData.personalUmbrella.coverables.locations;
            if (nonEditingLocation.length > 0) {
                index = newLocationsVM.value.findIndex((loc) => nonEditingLocation.indexOf(loc.publicID) === -1);
            }
            return newLocationsVM.getElement(index);
        });
    };

    const getUnderlyingPolicies = () => {
        let addedPolicy;
        if (underlyingPoliciesVM.value.findIndex((ulPolicy) => ulPolicy.ulPolicyFixID === 'new') === -1) {
            addedPolicy = pupModel.value.createUnderlyingPolicy({
                ulPolicyFixID: 'new',
                policyNumber: translator(messages.underlyingPolicyNotListed)
            });
        }

        const mappedValues = underlyingPoliciesVM.value.map((policy) => {
            let underlyingPolicyDisplayName = policy.getDisplayName(viewModelService.productMetadata, translator);
            if(isModernProduct && policy.policyType === 'HO') {
                underlyingPolicyDisplayName = underlyingPolicyDisplayName.replace('Homeowners', 'Property');
            }
            return {
                code: policy.ulPolicyFixID,
                name: translator({
                    id: policy.policyNumber,
                    defaultMessage: underlyingPolicyDisplayName
                })
            };
        });

        if (addedPolicy !== undefined) {
            pupModel.value.removeUnderlyingPolicy(addedPolicy);
        }
        return mappedValues;
    };

    const getPopupProps = (underlyingVM, isEdit = false) => {
        const componentProps = {
            title: messages.addPolicy,
            viewModel: underlyingVM,
            uiProps: addUnderlyingPolicyPopupMetadata.pageContent,
            actionBtnLabel: translator(messages.addPolicy),
            cancelBtnLabel: translator(messages.cancel),
            getOverrideProps: () => {
                return {
                    underlyingPolicyComponent: {
                        underlyingPolicyVM: underlyingVM,
                        quoteID: submissionVM.value.jobID,
                        isPolicyChange: true,
                        onValidate,
                        showValidationErrors,
                        isEdit,
                        authHeader,
                        MLAlertService,
                        isModernProduct
                    },
                };
            },
            customComponentResolvers: {
                underlyingPolicyComponent: UnderlyingPolicyComponent
            },
        };

        return componentProps;
    };

    const hasUnselectedTerms = (ulPolicy) => {
        const coverages = _.get(ulPolicy, 'ulPolicyCoverage.coverages.value');
        return coverages && coverages.some((c) => c.terms.some((t) => t.chosenTerm === undefined));
    };

    const validateBeforeSave = () => {
        const errorMsg = validatePrimaryLocation(location, locationsVM, policyAddress);
        setPrimaryLocationErrorMessage(errorMsg);
        return !!errorMsg;
    };

    const showAnimalExclusionWarningMessage = (param) => {
        const messageName = alertNames.locationAnimalExclusionWarning;
        clearManyBlockAlerts(messageName);
        if (param.shouldShow) {
            addWarningAlert(messages.requireAnimalLiabilityExclusionIncluded, messageName);
        }
    };

    const addUnderlyingPolicy = (ulPolicyVM) => {
        if (!(ulPolicyVM.aspects.valid && ulPolicyVM.aspects.subtreeValid && !hasUnselectedTerms(ulPolicyVM))) {
            setShowValidationErrors(true);
            setErrorTimestamp(errorTimestamp+1);
            return;
        }
        location.ulPolicyFixID.value = ulPolicyVM.ulPolicyFixID.value;
        updateWizardSubmissionData(submissionVM);
        return true;
    };

    const cancelAddUnderlyingPolicy = (policy) => {
        pupModel.value.removeUnderlyingPolicy(policy);
        location.ulPolicyFixID.value = undefined;
        updateWizardSubmissionData(submissionVM);
    };

    const createNewULPolicy = () => {
        const newUnderlyingPolicy = pupModel.value.createUnderlyingPolicy();
        const ulPolicy = underlyingPoliciesVM.findFirstElement(newUnderlyingPolicy);

        let componentProps = getPopupProps(ulPolicy);
        componentProps = {
            ...componentProps,
            onSaveAction: addUnderlyingPolicy
        };
        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).catch(() => cancelAddUnderlyingPolicy(ulPolicy.value));
    };

    const addNewLocationAlert = (item) => {
        const alertMsg = {
            alertName: alertNames.addedLocationName,
            formattedMessageName: 'xLocationHasBeenAdded',
            messagePram: item.value.address.city
        };
        
        clearBlockAlerts();
        
        const alertExists = alertMessages.some((msg) => {
            if (_.isEqual(msg, alertMsg)) {
                return true;
            }

            return false;
        });

        if (!alertExists) {
            alertMessages.push(alertMsg);
        }
    };

    const resetWhenLocationTypeVacantLand = (loc) => {
        if (loc.locationType.value !== undefined && loc.locationType.value.code === 'VacantLand') {
            loc.residenceType.value = undefined;
            loc.dwellingOccupancy.value = undefined;
            loc.dwellingUsage.value = undefined;
        }
    };

    const jumpToSelectedStepByStepID = (newStepID) => {
        MLEndorsementFlowUtil.addAndNavigateToSelectedStep(newStepID, steps, currentStepIndex, changeNextSteps, goNext, productCode)
    };

    const findLocationWithAnimalQuestion = (currentLocation) => {
        if (currentLocation) {
            return currentLocation.locationQuestionSet.questionSets.children.some((locationsQS) => {
                return locationsQS.aspects.valid;
            });
        }
    };

    const notVacantLand = (locationVM) => {
        return locationVM.locationType.value === undefined
            || locationVM.locationType.value.code !== 'VacantLand';
    };

    const getAnswerForAnimalQuestion = (questions, currentLocation) => {
        let isAnimalQuestionValid = false;

        if (questions && notVacantLand(currentLocation)) {
            questions.forEach((question) => {
                // check if "Do you have any animals?" question is true
                if (question.answers && question.answers.value.PUPPolicyLocationHasAnimals_ML === 'true') {
                    isAnimalQuestionValid = findLocationWithAnimalQuestion(currentLocation);
                }

                if (question.answers && question.answers.value.PUPPolicyLocationHasAnimals_ML === 'false') {
                    isAnimalQuestionValid = true
                }
            });
        } else {
            isAnimalQuestionValid = true
        }

        return isAnimalQuestionValid;
    };

     const isDataValid = () =>{
        let isQuestionValid;
        if(location.locationType == 'Dwelling')  {
            isQuestionValid  = getAnswerForAnimalQuestion(location.locationQuestionSet.questionSets, location);
        } else {
            isQuestionValid = true;

        }
        return isQuestionValid
     } 

    const validateDwellingFields = () => {
        
            const locationType = _.get(location,'locationType.value.code') || _.get(location,'locationType.value');
         
            clearBlockAlerts('Dwelling Fields Error');
        
            if (locationType !== 'Dwelling') {
                return false;
            }
         
            const residenceType = _.get(location,'residenceType.value');
            const dwellingOccupancy = _.get(location,'dwellingOccupancy.value');
            const dwellingUsage = _.get(location,'dwellingUsage.value');
         
            if (_.isNil(residenceType)) {
                const message = translator(messages.residenceTypeRequiredErrorMessage);
                addErrorAlert(message,'Dwelling Fields Error');
                window.scrollTo(0, 0);
                return true;
        
            }
         
            if (_.isNil(dwellingOccupancy)) {
                const message = translator(messages.dwellingOccupancyRequiredErrorMessage);
                addErrorAlert(message,'Dwelling Fields Error');
                window.scrollTo(0, 0);
                return true;
            }
         
            if (_.isNil(dwellingUsage)) {
               const message = translator(messages.dwellingUsageRequiredErrorMessage);
                addErrorAlert(message,'Dwelling Fields Error');
                window.scrollTo(0, 0);
                return true;
            }
         
            return false;
        
    };


    const onNext = () => {
        const isValid = location.aspects.valid && location.aspects.subtreeValid;

        if (!isValid || !isDataValid()) {
            setShowValidationErrors(true);
            setErrorTimestamp(errorTimestamp+1);

            return;
        }

        if (isModernProduct && validateDwellingFields()) {
            return;
        }

        if (validateBeforeSave()) {
            clearManyBlockAlerts([alertNames.primaryLocationError]);
            if (primaryLocationErrorMessage) {
                addErrorAlert(primaryLocationErrorMessage, alertNames.primaryLocationError);
            } 
            return;
        }

        if (location.ulPolicyFixID.value === 'new') {
            location.ulPolicyFixID.value = null;
        }

        resetWhenLocationTypeVacantLand(location);

        const item = location.value;
        const details = {
            data: `${item.address.addressLine1} ${item.address.city} ${item.address.state} ${item.address.postalCode}`,
            additionalData: { publicID: item.publicID }
        }

        MLNavigationStepsTrackingService.completeCurrentStep(details, authHeader, currentStep);
        const lbl = MLEndorsementUtil.stepLabel(currentStep);
        MLEndorsementUtil.setEndorsementDescription(lbl, submissionVM, translator);

        if (!location.value.isInEditMode) {
            addNewLocationAlert(location);
        }
        const action = location.value.isInEditMode ? 'Edit' : 'Add';
        MLTagManagerService.sendFormStepPolicyChangeStep(`${action} Location`);
        jumpToSelectedStepByStepID('endorsementAdditionalChanges'); 
    };

    const updateLocationVM = (selectedLocationVM) => {
        let selectedLocIndex = locationsVM.value.findIndex((loc) => nonEditingLocation.indexOf(loc.publicID) === -1);

        if (selectedLocIndex === -1) {
            selectedLocIndex = locationsVM.value.findIndex((loc) => loc.publicID === selectedLocationVM.value.publicID);
            _.set(locationsVM, `value[${selectedLocIndex}]`, selectedLocationVM.value);
        } else {
            _.set(locationsVM, `value[${selectedLocIndex}]`, selectedLocationVM.value);
        }

        refreshData();
    };

    const resolvers = {
        resolveComponentMap: {
            locationContentComponent: LocationPopupComponent
        },
        resolveCallbackMap: {
            onNext
        },
        resolveClassNameMap: styles
    };

    const overrides = {
        contentContainer: {
            className: isGPA ? 'page-content-gpa' : ''
        },
        addEditHeaderTitle: {
            content: location.value.isInEditMode ? translator(messages.editALocation) : translator(messages.addALocation)
        },
        addEditHeaderMsg: {
            content: location.value.isInEditMode ? translator(messages.editLocationDetails) : translator(messages.enterLocationDetails)
        },
        locationContentComponent: {
            pupModel,
            onValidate: onValidate,
            showValidationErrors,
            locationVM: location,
            syncExposure: syncLocation,
            getUnderlyingPolicies,
            validateBeforeSave,
            createUnderlyingPolicy: createNewULPolicy,
            onModelChange: updateLocationVM,           
            quoteID: submissionVM.jobID.value,
            submissionVM,
            isEndorsement: true,
            showActionRow: false,
            showAnimalExclusionWarningMessage,
            showProgressDialog
        },
        nextDescription: {
            content: getNextStepText()
        },
        stickyContinueSection: {
            visible: isGPA,
            defaultContinueSectionId: 'footer',
            nextDescription: getNextStepText(),
            onNext
        }
    };

    return (
        <WizardPage
            showCancel={false}
            showPrevious={false}
            showNext={false}
            policyChangePageHeaderML={isGPA ? messages.locations : null}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
                model={submissionVM}
                onModelChange={updateWizardData}
                componentMap={resolvers.resolveComponentMap}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                onValidationChange={onValidate}
                showErrors={showValidationErrors}
            />
            <ScrollToError counter={errorTimestamp} />
        </WizardPage>
    );
};

LocationPagePolChange.propTypes = wizardProps;
export default LocationPagePolChange;